import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
    Tooltip, IconButton, FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import api from '../services/api';
import { confirmAcao, toastSuccess } from '../services/alerts';
import { mostrarErro } from '../services/erros';
import { isVisualizador } from '../services/auth';

const calcularTotal = (qtd, valorSessao) => {
    const q = parseFloat(qtd);
    const v = parseFloat(valorSessao);
    return !isNaN(q) && !isNaN(v) && q > 0 ? (q * v).toFixed(2) : '';
};

export default function PlanoAtendimento() {
    const soLeitura = isVisualizador();
    const [planos, setPlanos] = useState([]);
    const [periodicidades, setPeriodicidades] = useState([]);
    const [valorPadraoSessao, setValorPadraoSessao] = useState(0);
    const [quantidadeBaseSessoes, setQuantidadeBaseSessoes] = useState('');
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingPlano, setEditingPlano] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const buildInitialState = (valorSessao = 0, qtdBase = '') => ({
        nome: '',
        descricao: '',
        quantidadeSessoes: qtdBase,
        valorTotal: calcularTotal(qtdBase, valorSessao),
        valorSessaoAvulsa: valorSessao,
        ativo: true,
        periodicidade: { id_periodicidade: '' }
    });
    const [formData, setFormData] = useState(buildInitialState());

    useEffect(() => {
        carregarPlanos();
        carregarPeriodicidades();
        carregarParametros();
    }, []);

    const carregarPlanos = async () => {
        setLoading(true);
        try {
            const response = await api.get('/planos-atendimento');
            const d = response.data;
            setPlanos(Array.isArray(d) ? d : (d?.content ?? []));
        } catch (error) {
            console.error('Erro ao carregar planos de atendimento:', error);
            mostrarMensagem('Erro ao carregar dados.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const carregarPeriodicidades = async () => {
        try {
            const response = await api.get('/periodicidades');
            setPeriodicidades(response.data.filter(p => p.ativo));
        } catch (error) {
            console.error('Erro ao carregar periodicidades:', error);
        }
    };

    const carregarParametros = async () => {
        try {
            const response = await api.get('/parametros');
            const valor = parseFloat(response.data.valorPadraoSessao) || 0;
            const qtdBase = response.data.quantidadeBaseSessoes || '';
            setValorPadraoSessao(valor);
            setQuantidadeBaseSessoes(qtdBase);
            setFormData(prev => ({
                ...prev,
                valorSessaoAvulsa: valor,
                quantidadeSessoes: qtdBase,
                valorTotal: calcularTotal(qtdBase, valor)
            }));
        } catch (error) {
            console.error('Erro ao carregar parâmetros:', error);
        }
    };


    const getQtdFromPeriodicidade = (idPeriodicidade) => {
        const per = periodicidades.find(p => p.id_periodicidade === idPeriodicidade);
        return per?.quantidadeSessoesBase || '';
    };

    const handleOpenDialog = (plano = null) => {
        if (plano) {
            setEditingPlano(plano);
            const qtd = plano.quantidadeSessoes || '';
            const valorSessao = plano.valorSessaoAvulsa || valorPadraoSessao;
            setFormData({
                id_p_atendimento: plano.id_p_atendimento,
                nome: plano.nome || '',
                descricao: plano.descricao || '',
                quantidadeSessoes: qtd,
                valorSessaoAvulsa: valorSessao,
                valorTotal: plano.valorTotal || calcularTotal(qtd, valorSessao),
                ativo: plano.ativo !== undefined ? plano.ativo : true,
                periodicidade: { id_periodicidade: plano.periodicidade?.id_periodicidade || '' }
            });
        } else {
            setEditingPlano(null);
            setFormData(buildInitialState(valorPadraoSessao, quantidadeBaseSessoes));
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingPlano(null);
    };

    const handleSave = async () => {
        if (!formData.nome || !formData.periodicidade.id_periodicidade) {
            mostrarMensagem('Preencha os campos obrigatórios (Nome e Periodicidade).', 'warning');
            return;
        }

        try {
            if (editingPlano) {
                await api.put(`/planos-atendimento/${editingPlano.id_p_atendimento}`, formData, { skipGlobalError: true });
                toastSuccess('Plano atualizado.');
            } else {
                await api.post('/planos-atendimento', formData, { skipGlobalError: true });
                toastSuccess('Plano criado.');
            }
            carregarPlanos();
            handleCloseDialog();
        } catch (error) {
            // O diálogo fica aberto: o erro costuma ser corrigível no próprio
            // formulário, e fechá-lo descartaria o que foi digitado.
            mostrarErro(error, 'salvar o plano');
        }
    };

    const handleDelete = async (id) => {
        const ok = await confirmAcao({
            title: 'Excluir plano?',
            text: 'Esta ação não pode ser desfeita.',
            confirmButtonText: 'Excluir',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;

        try {
            // skipGlobalError: o erro é exibido aqui pelo catálogo; sem isso o
            // interceptor mostraria um toast por cima.
            await api.delete(`/planos-atendimento/${id}`, { skipGlobalError: true });
            toastSuccess('Plano excluído.');
            carregarPlanos();
        } catch (error) {
            mostrarErro(error, 'excluir o plano');
        }
    };

    const mostrarMensagem = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                    Planos de Atendimento
                </Typography>
                {!soLeitura && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Novo Plano
                    </Button>
                )}
            </Box>

            <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                <TableCell><strong>Nome</strong></TableCell>
                                <TableCell><strong>Periodicidade</strong></TableCell>
                                <TableCell><strong>Qtd. Sessões</strong></TableCell>
                                <TableCell><strong>Valor Total (R$)</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell align="right"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!loading && planos.length > 0 ? planos.map((item) => (
                                <TableRow key={item.id_p_atendimento} hover>
                                    <TableCell sx={{ fontWeight: 500 }}>{item.nome}</TableCell>
                                    <TableCell>{item.periodicidade ? item.periodicidade.nome : '-'}</TableCell>
                                    <TableCell>{item.quantidadeSessoes}</TableCell>
                                    <TableCell>{item.valorTotal}</TableCell>
                                    <TableCell>{item.ativo ? 'Ativo' : 'Inativo'}</TableCell>
                                    <TableCell align="right">
                                        {!soLeitura ? (
                                            <>
                                                <Tooltip title="Editar">
                                                    <IconButton onClick={() => handleOpenDialog(item)} size="small" color="primary"><EditIcon /></IconButton>
                                                </Tooltip>
                                                <Tooltip title="Excluir">
                                                    <IconButton onClick={() => handleDelete(item.id_p_atendimento)} size="small" color="error"><DeleteIcon /></IconButton>
                                                </Tooltip>
                                            </>
                                        ) : (
                                            <Tooltip title="Visualizar">
                                                <IconButton onClick={() => handleOpenDialog(item)} size="small"><EditIcon /></IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">{loading ? 'Carregando...' : 'Nenhum plano encontrado.'}</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {soLeitura ? 'Visualizar Plano de Atendimento' : editingPlano ? 'Editar Plano de Atendimento' : 'Novo Plano de Atendimento'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3, pt: 1 }}>
                        <Box sx={{ gridColumn: 'span 12' }}>
                            <TextField fullWidth required label="Nome do Plano" name="nome" value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} disabled={soLeitura} />
                        </Box>

                        <Box sx={{ gridColumn: 'span 12' }}>
                            <FormControl fullWidth required>
                                <InputLabel>Periodicidade</InputLabel>
                                <Select
                                    value={formData.periodicidade.id_periodicidade}
                                    label="Periodicidade"
                                    onChange={(e) => {
                                        const idPer = e.target.value;
                                        const qtd = getQtdFromPeriodicidade(idPer);
                                        setFormData(prev => ({
                                            ...prev,
                                            periodicidade: { id_periodicidade: idPer },
                                            quantidadeSessoes: qtd,
                                            valorTotal: calcularTotal(qtd, prev.valorSessaoAvulsa)
                                        }));
                                    }}
                                    disabled={soLeitura}
                                >
                                    {periodicidades.map((per) => (
                                        <MenuItem key={per.id_periodicidade} value={per.id_periodicidade}>{per.nome}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ gridColumn: 'span 4' }}>
                            <TextField
                                fullWidth type="number" label="Qtd. Sessões" name="quantidadeSessoes"
                                value={formData.quantidadeSessoes}
                                onChange={(e) => {
                                    const qtd = e.target.value;
                                    setFormData(prev => ({
                                        ...prev,
                                        quantidadeSessoes: qtd,
                                        valorTotal: calcularTotal(qtd, prev.valorSessaoAvulsa)
                                    }));
                                }}
                                disabled={soLeitura}
                                helperText="Pré-preenchido pela periodicidade"
                            />
                        </Box>
                        <Box sx={{ gridColumn: 'span 4' }}>
                            <TextField
                                fullWidth type="number" inputProps={{ step: '0.01' }} label="Sessão Avulsa (R$)"
                                value={formData.valorSessaoAvulsa}
                                onChange={(e) => {
                                    const valor = e.target.value;
                                    setFormData(prev => ({
                                        ...prev,
                                        valorSessaoAvulsa: valor,
                                        valorTotal: calcularTotal(prev.quantidadeSessoes, valor)
                                    }));
                                }}
                                disabled={soLeitura}
                                helperText="Pré-preenchido pelo parâmetro"
                            />
                        </Box>
                        <Box sx={{ gridColumn: 'span 4' }}>
                            <TextField
                                fullWidth type="number" inputProps={{ step: '0.01' }} label="Valor Total (R$)"
                                value={formData.valorTotal}
                                disabled
                                helperText="Qtd. × valor da sessão"
                            />
                        </Box>

                        <Box sx={{ gridColumn: 'span 12' }}>
                            <TextField fullWidth label="Descrição do Plano" multiline rows={3} name="descricao" value={formData.descricao} onChange={(e) => setFormData({ ...formData, descricao: e.target.value })} disabled={soLeitura} />
                        </Box>

                        <Box sx={{ gridColumn: 'span 12' }}>
                             <FormControlLabel control={<Switch checked={formData.ativo} onChange={(e) => setFormData({...formData, ativo: e.target.checked})} disabled={soLeitura} />} label="Plano Ativo" />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseDialog}>{soLeitura ? 'Fechar' : 'Cancelar'}</Button>
                    {!soLeitura && (
                        <Button onClick={handleSave} variant="contained">
                            {editingPlano ? 'Salvar Alterações' : 'Criar Plano'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}